import { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import PrivacyTipOutlinedIcon from '@mui/icons-material/PrivacyTipOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { localize } from '../utils/interfaceCopy';
import WebviewMessenger from '../utils/WebviewMessenger';

export default function AdPrivacyOptions({ runtimeInfo, onError }) {
    const [pending, setPending] = useState(false);
    const requestRef = useRef(null);
    const errorRef = useRef(onError);
    errorRef.current = onError;
    useEffect(() => {
        const finish = result => {
            const request = requestRef.current;
            if (!request || result?.requestId !== request.id) return;
            if (result.status === 'pending') {
                clearTimeout(request.timer); // Reading the native form has no response deadline.
                return;
            }
            if (!['complete', 'error'].includes(result.status)) return;
            clearTimeout(request.timer);
            requestRef.current = null;
            setPending(false);
            if (result.status === 'error') errorRef.current?.(localize('광고 개인정보 설정을 불러오지 못했어요. 잠시 후 다시 시도해주세요.'));
        };
        const unsubscribe = WebviewMessenger.onMessage(message => {
            if (message.type === 'AD_PRIVACY_OPTIONS_RESULT') finish(message);
            if (message.type === 'APP_RUNTIME_INFO') finish(message.data?.adPrivacyOptionsResult);
        });
        return () => { clearTimeout(requestRef.current?.timer); requestRef.current = null; unsubscribe(); };
    }, []);

    const visible = WebviewMessenger.isInWebView()
        && runtimeInfo?.capabilities?.adPrivacyOptions === true
        && runtimeInfo?.adPrivacyOptionsRequired === true;
    if (!visible) return null;
    const busy = pending || runtimeInfo?.adPrivacyOptionsBusy === true;
    const open = () => {
        if (requestRef.current || runtimeInfo?.adPrivacyOptionsBusy === true) return;
        const request = { id: `ad-privacy-${Date.now()}-${Math.random().toString(36).slice(2)}` };
        requestRef.current = request;
        setPending(true);
        const fail = () => {
            if (requestRef.current !== request) return;
            clearTimeout(request.timer);
            requestRef.current = null;
            setPending(false);
            errorRef.current?.(localize('광고 개인정보 설정을 불러오지 못했어요. 잠시 후 다시 시도해주세요.'));
        };
        request.timer = setTimeout(fail, 5000); // Missing bridge receipt only, never a native form deadline.
        try { WebviewMessenger.send({ type: 'SHOW_AD_PRIVACY_OPTIONS', requestId: request.id }); }
        catch (_) { fail(); }
    };
    return <Box component="button" type="button" data-ad-privacy-options disabled={busy} onClick={open}
        aria-busy={busy} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', minHeight: 64,
            px: 0, py: 1.5, border: 0, borderBottom: '1px solid #f5f5f5', background: 'transparent',
            color: 'inherit', textAlign: 'left', cursor: busy ? 'wait' : 'pointer',
            '&:focus-visible': { outline: '2px solid #8a7000', outlineOffset: 2 } }}>
        <Box component="span" sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: '#f9fafb',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <PrivacyTipOutlinedIcon sx={{ color: '#8a7000', fontSize: 18 }} />
        </Box>
        <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
            <Typography component="span" sx={{ display: 'block', fontSize: '1.05rem', fontWeight: 700, color: '#1a1a1a',
                lineHeight: 1.5, overflowWrap: 'anywhere' }}>{localize('광고 개인정보 설정')}</Typography>
            <Typography component="span" sx={{ display: 'block', mt: 0.2, color: '#777', fontSize: '0.96rem',
                lineHeight: 1.5, overflowWrap: 'anywhere' }}>{localize('광고 동의 선택을 확인하거나 변경해요')}</Typography>
        </Box>
        {busy ? <CircularProgress size={18} sx={{ flexShrink: 0, color: '#8a7000' }} />
            : <ChevronRightIcon sx={{ flexShrink: 0, color: '#bbb', fontSize: 20 }} />}
    </Box>;
}
